import React, { useEffect, useState, useMemo } from "react";
import Modal from "../UIElements/Modal";
import { Switch } from "../UIElements/Switch";
import {
  ShoppingBag,
  Layers,
  CheckCircle2,
  Tag,
  Users,
  ShoppingCart,
  Clock,
  Calendar,
  ChevronDown,
  MapPin,
  Timer,
  DollarSign,
  CreditCard,
  Truck,
  AlertCircle,
} from "lucide-react";
import {
  usePromotionPaymethods,
  usePromotionDeliveryZones,
} from "../../../hooks/usePromotions";
import PromotionRulesSchedule, {
  createDefaultPromotionSchedule,
} from "./PromotionRulesSchedule";
import PromotionRulesCategories from "./PromotionRulesCategories";
import PromotionRulesProducts from "./PromotionRulesProducts";
import { ORDER_TYPE_OPTIONS } from "./discountsConstants";

const RuleCard = ({
  active,
  onToggle,
  icon: IconComponent,
  title,
  description,
  children,
  toggle,
  className = "",
}) => {
  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    if (onToggle) {
      onToggle();
    }
  };

  const isActive = Boolean(active);

  return (
    <div
      className={`flex flex-col justify-between overflow-hidden rounded-xl border transition-all duration-300 ${
        isActive
          ? "border-blue-500/50 bg-gray-900 shadow-lg shadow-blue-900/10"
          : "border-gray-800 bg-gray-900/60 hover:border-gray-700"
      } ${className}`}
    >
      <div
        role="button"
        tabIndex={0}
        aria-pressed={isActive}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        className="flex cursor-pointer items-start gap-3 p-4 select-none focus:ring-2 focus:ring-blue-500 focus:outline-none"
      >
        <div
          className={`rounded-lg p-2 transition-colors ${
            isActive ? "bg-blue-500 text-white" : "bg-gray-950 text-gray-500"
          }`}
        >
          {IconComponent ? <IconComponent size={18} /> : null}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between gap-3">
            <h5
              className={`truncate text-sm font-semibold ${
                isActive ? "text-white" : "text-gray-200"
              }`}
            >
              {title}
            </h5>
            {toggle ? (
              <div
                onClick={(event) => event.stopPropagation()}
                className="shrink-0"
              >
                {toggle}
              </div>
            ) : null}
          </div>
          <p className="text-xs leading-snug text-gray-400">{description}</p>
        </div>
      </div>

      {isActive && children ? (
        <div className="border-t border-gray-800/70 px-4 pt-0 pb-4">
          <div className="pt-3">{children}</div>
        </div>
      ) : null}
    </div>
  );
};

const PromotionRulesModal = ({
  isOpen,
  onClose,
  rules,
  onApplyRules,
  selectedBusinessIds,
  businesses,
  onQuickSave,
}) => {
  const [localRules, setLocalRules] = useState(rules || {});
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const { data: paymethodsData, isLoading: isLoadingPaymethods } =
    usePromotionPaymethods();

  const { data: deliveryZonesData, isLoading: isLoadingDeliveryZones } =
    usePromotionDeliveryZones({
      businessIds: selectedBusinessIds,
      enabled:
        isOpen &&
        Array.isArray(selectedBusinessIds) &&
        selectedBusinessIds.length > 0,
    });

  const paymethods = paymethodsData || [];
  const deliveryZones = deliveryZonesData?.zones || [];

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLocalRules(rules || {});
  }, [isOpen, rules]);

  const handleToggleRule = (key, isEnabled, defaultValue) => {
    setLocalRules((previous) => ({
      ...previous,
      [key]: isEnabled ? (previous[key] ?? defaultValue) : null,
      ...(key === "user_order_count"
        ? {
            user_order_count_condition: isEnabled ? "<" : null,
          }
        : null),
    }));
  };

  const handleChangeRuleValue = (key, value) => {
    setLocalRules((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleToggleFromBoolean = (key) => {
    setLocalRules((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const handleApply = () => {
    onApplyRules(localRules);
    onClose();
  };

  const isRuleEnabled = (key) => {
    const value = localRules?.[key];
    // A rule is considered enabled when it is not null/undefined.
    // Empty strings or empty arrays mean "configured but not filled yet",
    // so they should still be treated as enabled in the UI.
    if (Array.isArray(value)) {
      return value !== null && value !== undefined;
    }
    return value !== null && value !== undefined;
  };

  const selectedOrderTypes = useMemo(
    () => new Set(localRules?.order_types_allowed || []),
    [localRules?.order_types_allowed],
  );

  const selectedPaymethods = useMemo(
    () => new Set(localRules?.paymethods || []),
    [localRules?.paymethods],
  );

  const selectedDeliveryZones = useMemo(
    () => new Set(localRules?.delivery_zones || []),
    [localRules?.delivery_zones],
  );

  const handleToggleArrayItem = (key, id) => {
    setLocalRules((previous) => {
      const current = Array.isArray(previous[key]) ? previous[key] : [];
      const exists = current.includes(id);
      const next = exists
        ? current.filter((item) => item !== id)
        : [...current, id];
      return {
        ...previous,
        [key]: next.length === 0 ? null : next,
      };
    });
  };

  const handleChangeNumber = (key, nextValue) => {
    if (nextValue === "") {
      handleChangeRuleValue(key, "");
      return;
    }

    const numericRegex = /^[0-9]+$/;
    if (!numericRegex.test(nextValue)) {
      return;
    }
    handleChangeRuleValue(key, nextValue);
  };

  const handleChangeMinutes = (key, nextValue) => {
    handleChangeNumber(key, nextValue);
  };

  const handleChangeUserOrderCount = (nextValue) => {
    if (nextValue === "") {
      setLocalRules((previous) => ({
        ...previous,
        user_order_count: "",
        user_order_count_condition: "<",
      }));
      return;
    }

    const numericRegex = /^[0-9]+$/;
    if (!numericRegex.test(nextValue)) {
      return;
    }

    setLocalRules((previous) => ({
      ...previous,
      user_order_count: nextValue,
      user_order_count_condition: "<",
    }));
  };

  const {
    limit,
    limit_per_user,
    max_discount,
    minimum,
    user_order_count,
    valid_from_after_user_last_order_minutes,
    valid_until_after_user_last_order_minutes,
    include_options,
  } = localRules;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Advanced Rules & Conditions"
      subtitle="Define precise logic for when this discount applies."
      maxWidth="max-w-4xl"
      className="bg-gradient-to-b from-gray-950 to-gray-900"
    >
      <div className="space-y-8">
        {/* SCOPE */}
        <section className="space-y-4">
          <h4 className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-purple-300 uppercase">
            <ShoppingBag size={16} className="text-purple-300" />
            Scope
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <RuleCard
              active={isRuleEnabled("products")}
              onToggle={() =>
                handleToggleRule("products", !isRuleEnabled("products"), [])
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("products")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("products", enabled, [])
                  }
                />
              }
              icon={ShoppingCart}
              title="Specific Products"
              description="Limit to specific menu items by their IDs."
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-400">
                  Configure the specific products this promotion applies to.
                </p>
                <button
                  type="button"
                  onClick={() => setIsProductsModalOpen(true)}
                  className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-blue-500"
                  aria-label="Open products modal"
                >
                  Configure
                </button>
              </div>
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("categories")}
              onToggle={() =>
                handleToggleRule("categories", !isRuleEnabled("categories"), [])
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("categories")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("categories", enabled, [])
                  }
                />
              }
              icon={Layers}
              title="Specific Categories"
              description="Limit to whole categories like “Drinks”."
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-400">
                  Configure which categories are eligible for this promotion.
                </p>
                <button
                  type="button"
                  onClick={() => setIsCategoriesModalOpen(true)}
                  className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-blue-500"
                  aria-label="Open categories modal"
                >
                  Configure
                </button>
              </div>
            </RuleCard>

            <RuleCard
              active={Boolean(include_options)}
              onToggle={() => handleToggleFromBoolean("include_options")}
              toggle={
                <Switch
                  checked={Boolean(include_options)}
                  onCheckedChange={() =>
                    handleToggleFromBoolean("include_options")
                  }
                />
              }
              icon={CheckCircle2}
              title="Include Options"
              description="Apply discount to modifiers & add-ons?"
              className="md:col-span-2"
            >
              <p className="text-xs text-gray-400">
                When enabled, the discount applies to the total product price
                including add-ons.
              </p>
            </RuleCard>
          </div>
        </section>

        {/* LIMITS */}
        <section className="space-y-4">
          <h4 className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-blue-300 uppercase">
            <Tag size={16} className="text-blue-300" />
            Limits
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <RuleCard
              active={isRuleEnabled("limit")}
              onToggle={() =>
                handleToggleRule("limit", !isRuleEnabled("limit"), "")
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("limit")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("limit", enabled, "")
                  }
                />
              }
              icon={Tag}
              title="Max Total Usage"
              description="Total redemptions allowed globally."
            >
              <input
                type="text"
                inputMode="numeric"
                value={limit ?? ""}
                onChange={(event) =>
                  handleChangeNumber("limit", event.target.value)
                }
                className="w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g. 1000 total redemptions"
                aria-label="Max total usage value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("limit_per_user")}
              onToggle={() =>
                handleToggleRule(
                  "limit_per_user",
                  !isRuleEnabled("limit_per_user"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("limit_per_user")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("limit_per_user", enabled, "")
                  }
                />
              }
              icon={Users}
              title="Max Per User"
              description="Redemptions allowed per customer."
            >
              <input
                type="text"
                inputMode="numeric"
                value={limit_per_user ?? ""}
                onChange={(event) =>
                  handleChangeNumber("limit_per_user", event.target.value)
                }
                className="w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g. 1 use per customer"
                aria-label="Max per user value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("user_order_count")}
              onToggle={() =>
                handleToggleRule(
                  "user_order_count",
                  !isRuleEnabled("user_order_count"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("user_order_count")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("user_order_count", enabled, "")
                  }
                />
              }
              icon={ShoppingCart}
              title="Order History Limit"
              description="Max previous orders on platform."
            >
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <span className="text-gray-400">User has less than</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={user_order_count ?? ""}
                  onChange={(event) =>
                    handleChangeUserOrderCount(event.target.value)
                  }
                  className="w-16 rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-center text-sm text-white placeholder-gray-600 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="5"
                  aria-label="User has less than order count"
                />
                <span className="text-gray-400">orders</span>
              </div>
            </RuleCard>
          </div>
        </section>

        {/* TIME & LOCATION */}
        <section className="space-y-4">
          <h4 className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-blue-300 uppercase">
            <Clock size={16} className="text-blue-300" />
            Time &amp; Location
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <RuleCard
              active={isRuleEnabled("schedule")}
              onToggle={() =>
                handleToggleRule(
                  "schedule",
                  !isRuleEnabled("schedule"),
                  createDefaultPromotionSchedule(),
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("schedule")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule(
                      "schedule",
                      enabled,
                      createDefaultPromotionSchedule(),
                    )
                  }
                />
              }
              icon={Calendar}
              title="Schedule Limit"
              description="Specific days and times."
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-400">
                  Configure the schedule for this promotion.
                </p>
                <button
                  type="button"
                  onClick={() => setIsScheduleModalOpen(true)}
                  className="inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-blue-500"
                  aria-label="Open schedule modal"
                >
                  Configure
                </button>
              </div>
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("delivery_zones")}
              onToggle={() =>
                handleToggleRule(
                  "delivery_zones",
                  !isRuleEnabled("delivery_zones"),
                  [],
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("delivery_zones")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("delivery_zones", enabled, [])
                  }
                />
              }
              icon={MapPin}
              title="Delivery Zones"
              description="Restrict to specific areas."
            >
              <div className="space-y-2">
                {(!Array.isArray(selectedBusinessIds) ||
                  selectedBusinessIds.length === 0) && (
                  <p className="text-xs text-gray-500">
                    Select at least one store to load delivery zones.
                  </p>
                )}
                {isLoadingDeliveryZones && (
                  <p className="text-xs text-gray-500">
                    Loading delivery zones...
                  </p>
                )}
                {!isLoadingDeliveryZones && deliveryZones.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No delivery zones available.
                  </p>
                )}

                {!isLoadingDeliveryZones && deliveryZones.length > 0 ? (
                  <div className="relative">
                    <select
                      value={Array.from(selectedDeliveryZones)[0] ?? ""}
                      onChange={(event) => {
                        const nextId = Number(event.target.value);
                        if (!Number.isFinite(nextId)) {
                          handleChangeRuleValue("delivery_zones", null);
                          return;
                        }
                        handleChangeRuleValue("delivery_zones", [nextId]);
                      }}
                      className="w-full cursor-pointer appearance-none rounded-lg border border-gray-800 bg-gray-950 py-2.5 pr-10 pl-4 text-white transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500"
                      aria-label="Select delivery zone"
                    >
                      <option value="">Select delivery zone</option>
                      {deliveryZones.map((zone) => (
                        <option key={zone.id} value={zone.id}>
                          {zone.name} ({zone.minimum} - {zone.price})
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={16}
                      className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-gray-500"
                    />
                  </div>
                ) : null}
              </div>
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("valid_from_after_user_last_order_minutes")}
              onToggle={() =>
                handleToggleRule(
                  "valid_from_after_user_last_order_minutes",
                  !isRuleEnabled("valid_from_after_user_last_order_minutes"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled(
                    "valid_from_after_user_last_order_minutes",
                  )}
                  onCheckedChange={(enabled) =>
                    handleToggleRule(
                      "valid_from_after_user_last_order_minutes",
                      enabled,
                      "",
                    )
                  }
                />
              }
              icon={Timer}
              title="Minutes From Last Order"
              description="Minimum cooldown time."
            >
              <input
                type="text"
                inputMode="numeric"
                value={valid_from_after_user_last_order_minutes ?? ""}
                onChange={(event) =>
                  handleChangeMinutes(
                    "valid_from_after_user_last_order_minutes",
                    event.target.value,
                  )
                }
                className="w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g. 60 minutes"
                aria-label="Minutes from last order value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled(
                "valid_until_after_user_last_order_minutes",
              )}
              onToggle={() =>
                handleToggleRule(
                  "valid_until_after_user_last_order_minutes",
                  !isRuleEnabled("valid_until_after_user_last_order_minutes"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled(
                    "valid_until_after_user_last_order_minutes",
                  )}
                  onCheckedChange={(enabled) =>
                    handleToggleRule(
                      "valid_until_after_user_last_order_minutes",
                      enabled,
                      "",
                    )
                  }
                />
              }
              icon={Timer}
              title="Minutes Until Last Order"
              description="Maximum inactivity time."
            >
              <input
                type="text"
                inputMode="numeric"
                value={valid_until_after_user_last_order_minutes ?? ""}
                onChange={(event) =>
                  handleChangeMinutes(
                    "valid_until_after_user_last_order_minutes",
                    event.target.value,
                  )
                }
                className="w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g. 1440 minutes"
                aria-label="Minutes until last order value"
              />
            </RuleCard>
          </div>
        </section>

        {/* CART & PAYMENT */}
        <section className="space-y-4">
          <h4 className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-green-300 uppercase">
            <DollarSign size={16} className="text-green-300" />
            Cart &amp; Payment
          </h4>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <RuleCard
              active={isRuleEnabled("minimum")}
              onToggle={() =>
                handleToggleRule("minimum", !isRuleEnabled("minimum"), "")
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("minimum")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("minimum", enabled, "")
                  }
                />
              }
              icon={ShoppingCart}
              title="Minimum Purchase"
              description="Required cart subtotal."
            >
              <input
                type="text"
                inputMode="numeric"
                value={minimum ?? ""}
                onChange={(event) =>
                  handleChangeNumber("minimum", event.target.value)
                }
                className="w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g. 20 (currency units)"
                aria-label="Minimum purchase value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("max_discount")}
              onToggle={() =>
                handleToggleRule(
                  "max_discount",
                  !isRuleEnabled("max_discount"),
                  "",
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("max_discount")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("max_discount", enabled, "")
                  }
                />
              }
              icon={AlertCircle}
              title="Max Discount Limit"
              description="Cap the discount amount."
            >
              <input
                type="text"
                inputMode="numeric"
                value={max_discount ?? ""}
                onChange={(event) =>
                  handleChangeNumber("max_discount", event.target.value)
                }
                className="w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g. 20 (USD)"
                aria-label="Max discount limit value"
              />
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("paymethods")}
              onToggle={() =>
                handleToggleRule("paymethods", !isRuleEnabled("paymethods"), [])
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("paymethods")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("paymethods", enabled, [])
                  }
                />
              }
              icon={CreditCard}
              title="Payment Methods"
              description="Allowed payment types."
            >
              <div className="flex max-h-[118px] flex-wrap gap-2 overflow-y-auto rounded-md border border-gray-800 bg-gray-950/60 p-2">
                {isLoadingPaymethods && (
                  <p className="text-xs text-gray-500">
                    Loading payment methods...
                  </p>
                )}
                {!isLoadingPaymethods && paymethods.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No payment methods available.
                  </p>
                )}
                {!isLoadingPaymethods &&
                  paymethods.map((paymethod) => (
                    <label
                      key={paymethod.id}
                      className={`inline-flex max-w-full cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors select-none ${
                        selectedPaymethods.has(paymethod.id)
                          ? "border-blue-500/40 bg-blue-600/15 text-white"
                          : "border-gray-800 bg-gray-900/40 text-gray-200 hover:bg-gray-900/70"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-500 bg-gray-900 accent-blue-500"
                        checked={selectedPaymethods.has(paymethod.id)}
                        onChange={() =>
                          handleToggleArrayItem("paymethods", paymethod.id)
                        }
                        aria-label={`Toggle paymethod ${
                          paymethod.name ||
                          paymethod.gateway ||
                          "payment method"
                        }`}
                      />
                      <span className="max-w-[160px] truncate">
                        {paymethod.name || paymethod.gateway}
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={selectedPaymethods.has(paymethod.id)}
                        onChange={() =>
                          handleToggleArrayItem("paymethods", paymethod.id)
                        }
                      />
                    </label>
                  ))}
              </div>
            </RuleCard>

            <RuleCard
              active={isRuleEnabled("order_types_allowed")}
              onToggle={() =>
                handleToggleRule(
                  "order_types_allowed",
                  !isRuleEnabled("order_types_allowed"),
                  [],
                )
              }
              toggle={
                <Switch
                  checked={isRuleEnabled("order_types_allowed")}
                  onCheckedChange={(enabled) =>
                    handleToggleRule("order_types_allowed", enabled, [])
                  }
                />
              }
              icon={Truck}
              title="Order Type"
              description="Delivery, Pickup, etc."
            >
              <div className="grid grid-cols-2 gap-2">
                {ORDER_TYPE_OPTIONS.map((typeOption) => (
                  <button
                    key={typeOption.value}
                    type="button"
                    onClick={() =>
                      handleToggleArrayItem(
                        "order_types_allowed",
                        typeOption.value,
                      )
                    }
                    className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
                      selectedOrderTypes.has(typeOption.value)
                        ? "border-blue-500/60 bg-blue-600/20 text-blue-200"
                        : "border-gray-800 bg-gray-950 text-gray-300 hover:bg-gray-800"
                    }`}
                    aria-label={`Toggle order type ${typeOption.label}`}
                  >
                    {typeOption.label}
                  </button>
                ))}
              </div>
            </RuleCard>
          </div>
        </section>

        <div className="flex items-center justify-between border-t border-gray-800 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-400 transition-colors hover:text-gray-200"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="inline-flex items-center justify-center rounded-lg bg-purple-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-purple-500/30 transition-colors hover:bg-purple-500"
          >
            Save Rules
          </button>
        </div>
      </div>
      {/* Products rule modal (design-only for now) */}
      <Modal
        isOpen={isProductsModalOpen}
        onClose={() => setIsProductsModalOpen(false)}
        title="Products rule"
        subtitle="Select products to include or exclude for this promotion."
        maxWidth="max-w-xl"
        className="bg-gray-950"
      >
        <div className="space-y-4">
          <PromotionRulesProducts
            selectedBusinessIds={selectedBusinessIds}
            businesses={businesses}
            value={localRules?.products}
            onChange={(nextValue) => {
              handleChangeRuleValue("products", nextValue);
            }}
          />

          <div className="flex items-center justify-end gap-2 border-t border-gray-800 pt-2">
            <button
              type="button"
              onClick={() => {
                if (onQuickSave) {
                  onQuickSave({ products: localRules?.products ?? null });
                }
                setIsProductsModalOpen(false);
              }}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-300 transition-colors hover:bg-gray-800 hover:text-white"
              aria-label="Close products modal"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>

      {/* Categories rule modal (design-only for now) */}
      <Modal
        isOpen={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        title="Categories rule"
        subtitle="Select categories to include or exclude for this promotion."
        maxWidth="max-w-xl"
        className="bg-gray-950"
      >
        <div className="space-y-4">
          <PromotionRulesCategories
            selectedBusinessIds={selectedBusinessIds}
            businesses={businesses}
            value={localRules?.categories}
            onChange={(nextValue) => {
              handleChangeRuleValue("categories", nextValue);
            }}
          />

          <div className="flex items-center justify-end gap-2 border-t border-gray-800 pt-2">
            <button
              type="button"
              onClick={() => {
                if (onQuickSave) {
                  onQuickSave({ categories: localRules?.categories ?? null });
                }
                setIsCategoriesModalOpen(false);
              }}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-300 transition-colors hover:bg-gray-800 hover:text-white"
              aria-label="Close categories modal"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>

      {/* Schedule rule modal (design-only for now) */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        title="Schedule rule"
        subtitle="Configure the schedule for when this promotion is active."
        maxWidth="max-w-xl"
        className="bg-gray-950"
      >
        <div className="space-y-4">
          <PromotionRulesSchedule
            schedule={
              Array.isArray(localRules?.schedule) ? localRules.schedule : []
            }
            onScheduleChange={(nextSchedule) => {
              handleChangeRuleValue("schedule", nextSchedule);
            }}
          />

          <div className="flex items-center justify-end gap-2 border-t border-gray-800 pt-2">
            <button
              type="button"
              onClick={() => setIsScheduleModalOpen(false)}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-300 transition-colors hover:bg-gray-800 hover:text-white"
              aria-label="Close schedule modal"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default PromotionRulesModal;
